import React, {ReactNode, useMemo, useState} from "react";
import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Field, Label,} from "headlessui-v2";
import {twJoin} from "tailwind-merge";
import {Badge} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {ChevronUpDownIcon} from "@heroicons/react/16/solid";
import {comboboxOptionsClassName} from "@widgets/input/combobox/combobox";
import RadioCheckIndicator from "@widgets/pos/order/menu/product/options/option/RadioCheckIndicator";

export type MultiSelectOption<T extends string | number> = {
    value: T,
    displayValue: string,
    description?: ReactNode, // Shown next to the name in the list
};

type Props<T extends string | number> = {
    label: ReactNode,
    tip?: ReactNode,
    options: MultiSelectOption<T>[],
    value: T[],
    onChange: (value: T[]) => void,
    disabled?: boolean,
    // Show the chosen options as badges below the input
    showSelected?: boolean,
};

/**
 * Pick several options from a list, with search. Rendered in place (no
 * portal), so it also works inside modals.
 */
const MultiSelectCombobox = <T extends string | number>({
                                                            label,
                                                            tip,
                                                            options,
                                                            value,
                                                            onChange,
                                                            disabled = false,
                                                            showSelected = true,
                                                        }: Props<T>) => {
    const intl = useIntl();
    const [query, setQuery] = useState("");
    const filteredOptions: MultiSelectOption<T>[] = useMemo(() => {
        const search = query.trim().toLowerCase();
        return search === ""
            ? options
            : options.filter(option => option.displayValue.toLowerCase().includes(search));
    }, [options, query]);
    const selectedOptions: MultiSelectOption<T>[] = useMemo(
        () => options.filter(option => value.includes(option.value)),
        [options, value]);

    return (
        <Field disabled={disabled} className={"w-full flex flex-col gap-2"}>
            <Label as={"div"}>{label}</Label>

            <Combobox
                multiple
                immediate
                value={value}
                onChange={(newValue: T[]) => onChange(newValue)}
                onClose={() => setQuery("")}
                disabled={disabled}
            >
                <div className={"relative w-full"}>
                    <ComboboxInput
                        className={twJoin(
                            "block w-full rounded-md border-0 py-1.5 pl-3 pr-10 shadow-sm sm:text-sm sm:leading-6",
                            "text-gray-900 bg-white ring-1 ring-inset ring-gray-300 placeholder:text-gray-500",
                            "focus:ring-2 focus:ring-inset focus:ring-blue-600",
                            "dark:bg-slate-800 dark:text-white dark:ring-slate-600 dark:placeholder:text-slate-400",
                            "data-[disabled]:cursor-not-allowed data-[disabled]:bg-gray-50 data-[disabled]:text-gray-500",
                        )}
                        placeholder={
                            selectedOptions.length === 0
                                ? intl.formatMessage({id: "searchOrChoose"})
                                : intl.formatMessage({id: "nChosen"}, {count: selectedOptions.length})
                        }
                        onChange={event => setQuery(event.target.value)}
                    />
                    <ComboboxButton className={"group absolute inset-y-0 right-0 flex items-center px-2.5"}>
                        <ChevronUpDownIcon className={"size-4 text-gray-400 group-data-[disabled]:opacity-50"}/>
                    </ComboboxButton>

                    <ComboboxOptions
                        transition
                        className={twJoin(
                            comboboxOptionsClassName,
                            "absolute mt-1.5 w-full max-h-64 overflow-y-auto py-1",
                            "transition duration-150 data-[closed]:opacity-0 data-[closed]:-translate-y-1",
                        )}
                    >
                        {
                            filteredOptions.length === 0 &&
                            <div className={"w-full px-4 py-2 text-sm text-gray-500 dark:text-slate-400"}>
                                <FormattedMessage id={"noResults"}/>
                            </div>
                        }

                        {filteredOptions.map(option =>
                            <ComboboxOption
                                key={option.value}
                                value={option.value}
                                className={twJoin(
                                    "group/item w-full flex flex-row items-center gap-3 px-3 py-1.5 cursor-pointer text-sm",
                                    "data-[focus]:bg-gray-100 dark:data-[focus]:bg-slate-700",
                                )}
                            >
                                {({selected}) => (
                                    <>
                                        <RadioCheckIndicator canSelectMultiple selected={selected}/>
                                        <span className={"font-medium text-gray-900 dark:text-white"}>
                                            {option.displayValue}
                                        </span>
                                        {
                                            option.description &&
                                            <span
                                                className={"ml-auto text-gray-500 dark:text-slate-400 whitespace-nowrap"}>
                                                {option.description}
                                            </span>
                                        }
                                    </>
                                )}
                            </ComboboxOption>
                        )}
                    </ComboboxOptions>
                </div>
            </Combobox>

            {
                showSelected && selectedOptions.length > 0 &&
                <div className={"flex flex-wrap gap-1.5"}>
                    {selectedOptions.map(option =>
                        <Badge
                            key={option.value}
                            size={"xs"}
                            color={"secondary"}
                            variant={"flat"}
                            radius={"chip"}
                        >
                            {option.displayValue}
                        </Badge>
                    )}
                </div>
            }

            {
                tip &&
                <p className={"text-sm text-gray-500 dark:text-slate-400"}>
                    {tip}
                </p>
            }
        </Field>
    );
};

export default MultiSelectCombobox;